import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MomentHelper } from '../helpers/moment-helper';
import { AttributeFilters } from '../models/attribute-filters';
import { PropertyType } from '../models/enums/property-type';
import { FilterCriteria } from '../models/filter-criteria';
import { IFilterable } from '../models/interfaces/filterable.interface';
import { AttributeDefinition } from '../models/server/attribute-definition';
import { ColumnDefinition } from '../models/server/column-definition';
import { FolderSearchStateService } from '../services/state/folder-search-state.service';

/**
 * Gestionnaire de filtres pour les tableaux.
 * S'applique uniquement sur les colonnes visibles du tableaux.
 */
export class TableFilterHandler {
    /** Utilisé pour gérer la désincription des observables lorsque la classe est détruite. */
    protected classDisposed$: Subject<void> = new Subject<void>();

    /** Formulaire contenant les filtres du tableau de résultats. */
    filtersForm: UntypedFormGroup;

    /** Valeurs des filtres saisis par l'utilisateur. */
    filterValues: AttributeFilters = new AttributeFilters();

    /** Définitions des colonnes */
    displayedColumnsDefinitions: ColumnDefinition[];

    /** Définitions des attributs */
    attributeFilters: AttributeDefinition[];

    /**
     * Constructeur.
     */
    constructor(
        private sourceComponent: IFilterable,
        private folderFilter: FolderSearchStateService = null
    ) {
        this.filtersForm = new UntypedFormGroup({});

        if (folderFilter) {
            folderFilter.setFilterHandler(this);
        }
    }

    /**
     * Initialise les filtres du tableau de dossiers.
     * @param displayedColumnsDefinitions : Définitions des colonnes.
     */
    initializeFilters(displayedColumnsDefinitions: ColumnDefinition[]) {
        this.displayedColumnsDefinitions = displayedColumnsDefinitions;

        // Crée un contrôle de filtre dans l'en-tête de chaque colonne.
        this.displayedColumnsDefinitions.forEach((def) => {
            this.initializeFilter(def);
        });
    }

    /**
     * Initialise le filtre d'une colonne.
     * @param columnDef: Definition de la commande.
     */
    initializeFilter(columnDef: ColumnDefinition) {
        // Création du contrôle dans l'en-tête de la colonne.
        this.filtersForm.addControl(columnDef.propertyCode, new UntypedFormControl());

        // Initialisation du contrôle à vide.
        this.filterValues[columnDef.propertyCode] = null;

        // Abonnement aux événements ValueChanges du contrôle afin d'appliquer le filtre quand l'utilisateur saisie une valeur.
        this.filtersForm.controls[columnDef.propertyCode].valueChanges.pipe(takeUntil(this.classDisposed$)).subscribe((val) => {
            if (columnDef.type === PropertyType.Date) {
                if (!!val && val.length == 10) {
                    this.applyColumnFilter(val, columnDef);
                } else if (this.filterValues[columnDef.propertyCode]) {
                    // Si le texte saisi est vide mais que la valeur du filtre n'est pas vide.
                    this.resetColumnFilter(columnDef.propertyCode);
                }
            } else {
                if (!!val && val.length != 0) {
                    this.applyColumnFilter(val, columnDef);
                } else if (this.filterValues[columnDef.propertyCode]) {
                    // Si le texte saisi est vide mais que la valeur du filtre n'est pas vide.
                    this.resetColumnFilter(columnDef.propertyCode);
                }
            }
        });
    }

    /**
     * Spécifie la valeur pour le filtre d'une colonne et l'applique.
     * @param filterValue: Valeur du filtre.
     * @param columnDef: Definition de la colonne.
     */
    public applyColumnFilter(filterValue: any, columnDef: ColumnDefinition) {
        if (columnDef.type === PropertyType.Date) {
            filterValue = MomentHelper.formatAsIso8601Date(filterValue as Moment);
        } else if (columnDef.type === PropertyType.DateAndTime) {
            filterValue = MomentHelper.formatAsIso8601DateTime(filterValue as Moment);
        }

        let array: string[] = [];

        if (!Array.isArray(filterValue)) {
            array.push(filterValue.toString());
        } else {
            array = filterValue;
        }

        const filterCriteria = new FilterCriteria(columnDef.type, array);
        this.filterValues[columnDef.propertyCode] = filterCriteria;

        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée puis applique le filtre.
     * columnDef: Définition de la colonne dont le filtre doit être réinitialisé.
     */
    public resetColumnFilter(columnDef: string): void {
        this.filterValues[columnDef] = null;
        this.filtersForm.controls[columnDef].reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise les filtres de toutes les colonnes puis applique les filtres.
     */
    public resetAllColumnsFilters(): void {
        if (!!this.displayedColumnsDefinitions) {
            this.displayedColumnsDefinitions.forEach((item, index) => {
                this.filterValues[item.propertyCode] = null;
            });
        }
        this.filtersForm.reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Specifie les filtres à appliquer
     * @param filters les filtres à appliquer
     */
    public setFilters(filters: AttributeFilters) {
        this.filterValues = filters;
    }

    /**
     * Récupère les filtres à appliquer
     * @retourne Les filtres à appliquer
     */
    public getFilters(): AttributeFilters {
        return this.filterValues;
    }

    public dispose() {
        this.classDisposed$.next();
        this.classDisposed$.complete();
    }
}
